import { CatalogItem } from '../services/product-service';

export interface O365AddonGroup<T> {
  groupKey: string;
  items: T[];
}

/** Clave estable para agrupar variantes monthly/annual del mismo add-on. */
export function getO365AddonGroupKey(
  item: Pick<CatalogItem, 'externalOfferId' | 'serviceCode' | 'productId'>
): string {
  if (item.externalOfferId) {
    return `offer:${item.externalOfferId}`;
  }
  if (item.serviceCode != null) {
    return `service:${item.serviceCode}`;
  }
  return `product:${item.productId}`;
}

function billingCycleRank(cycle?: CatalogItem['billingCycle']): number {
  return cycle === 'annual' ? 1 : 0;
}

/** Agrupa add-ons del catálogo por externalOfferId; monthly antes que annual. */
export function groupO365CatalogAddons(addons: CatalogItem[]): O365AddonGroup<CatalogItem>[] {
  const order: string[] = [];
  const groups = new Map<string, CatalogItem[]>();

  for (const addon of addons) {
    const key = getO365AddonGroupKey(addon);
    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(addon);
  }

  return order.map((groupKey) => ({
    groupKey,
    items: groups.get(groupKey)!.sort(
      (left, right) => billingCycleRank(left.billingCycle) - billingCycleRank(right.billingCycle)
    ),
  }));
}

/** Agrupa líneas de summary O365 por externalOfferId manteniendo el orden del catálogo. */
export function groupO365SummaryAddonLines<T extends { addonProductId?: string; id: string }>(
  addons: T[],
  resolveCatalogItem: (addonProductId: string) => CatalogItem | undefined
): O365AddonGroup<T>[] {
  const order: string[] = [];
  const groups = new Map<string, T[]>();

  for (const addon of addons) {
    const addonProductId = addon.addonProductId ?? addon.id;
    const catalog = resolveCatalogItem(addonProductId);
    const key = getO365AddonGroupKey(
      catalog ?? { productId: addonProductId, externalOfferId: undefined, serviceCode: undefined }
    );

    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(addon);
  }

  return order.map((groupKey) => ({
    groupKey,
    items: groups.get(groupKey)!.sort((left, right) => {
      const leftCatalog = resolveCatalogItem(left.addonProductId ?? left.id);
      const rightCatalog = resolveCatalogItem(right.addonProductId ?? right.id);
      return (
        billingCycleRank(leftCatalog?.billingCycle) - billingCycleRank(rightCatalog?.billingCycle)
      );
    }),
  }));
}

/** Ordena entradas genéricas según el catálogo del add-on padre. */
export function sortByO365AddonGroups<T>(
  entries: T[],
  resolveAddonProductId: (entry: T) => string,
  resolveCatalogItem: (addonProductId: string) => CatalogItem | undefined
): T[] {
  const order: string[] = [];
  const groups = new Map<string, T[]>();

  for (const entry of entries) {
    const addonProductId = resolveAddonProductId(entry);
    const catalog = resolveCatalogItem(addonProductId);
    const key = getO365AddonGroupKey(
      catalog ?? { productId: addonProductId, externalOfferId: undefined, serviceCode: undefined }
    );

    if (!groups.has(key)) {
      groups.set(key, []);
      order.push(key);
    }
    groups.get(key)!.push(entry);
  }

  return order.flatMap((groupKey) =>
    groups.get(groupKey)!.sort((left, right) => {
      const leftCatalog = resolveCatalogItem(resolveAddonProductId(left));
      const rightCatalog = resolveCatalogItem(resolveAddonProductId(right));
      return (
        billingCycleRank(leftCatalog?.billingCycle) - billingCycleRank(rightCatalog?.billingCycle)
      );
    })
  );
}
